<script setup lang="ts">
import { ref } from 'vue'
import { UiTwoToneColor } from '@afs/design-system'
import type { UiHeaderCta } from '@afs/design-system/components/organisms/UiHeader/interface.d.ts'

import type UiHeader from '@afs/design-system/components/organisms/UiHeader/UiHeader.vue.d.ts'

import { useI18n } from 'vue-i18n'
import { usePageContext } from 'vike-vue/usePageContext'
import { useGtmHeaderClick } from '@/composables/useGtmTag'

const { t } = useI18n()

const headerElement = ref<InstanceType<typeof UiHeader> | null>(null)
const isMenuOpen = ref<boolean>(false)
const isHelpModalOpen = ref<boolean>(false)
const activeTrigger = ref<string>('')

const track = useGtmHeaderClick()

defineExpose({
  headerElement,
})

const { header, menu } = usePageContext()

const skiplinksData = {
  label: t('skiplinks.label'),
  links: [
    { id: 'skiplinks-main-content', text: t('skiplinks.mainContent'), to: '#main-content', external: true },
    { id: 'skiplinks-main-menu', text: t('skiplinks.mainMenu'), to: '#main-menu', external: true },
    { id: 'skiplinks-main-footer', text: t('skiplinks.mainFooter'), to: '#main-footer', external: true },
  ],
}

const accountCta: UiHeaderCta = {
  text: t('header.ctas.customer-area.label'),
  href: import.meta.env.PUBLIC_ENV__ESPACE_MEIA_BASEPATH,
  prependIcon: 'account-plain',
}

const initCtas = (): UiHeaderCta[] => {
  // Initialisation des CTAs avec le bouton "Aide et contact"
  const ctas = [
    ...(header?.ctas ?? []).map((cta) => {
      return {
        ...cta,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick: (e: any) => {
          // Soit on redirige vers la page contact, soit on tente d'ouvrir la pop-in avec les infos de contact
          if (e.target?.innerText && e.target.href) track(e.target.innerText, e.target.href)
          else isHelpModalOpen.value = true
        },
      }
    }),
  ]

  // On affiche le bouton "Espace client" uniquement pour le site Meia, pas pour les sites partenaires
  if (!header?.partnerLogo)
    ctas.push({
      ...accountCta,
      id: 'customer-area',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onClick: (e: any) => {
        if (e.target?.innerText && e.target.href) {
          track(e.target.innerText, e.target.href)
        }
      },
      mobileVisible: true,
    })

  return ctas
}

const ctas = initCtas()
</script>

<template>
  <div class="Common-header">
    <molecules-ui-skiplinks v-bind="skiplinksData" />
    <organisms-ui-header
      ref="headerElement"
      v-model:is-menu-open="isMenuOpen"
      :logo="header?.logo"
      :ctas="ctas"
      :burger-label="$t('header.burgerLabel')"
      :legal-message="header?.legalMessage"
      :partner-logo="header?.partnerLogo"
      :link-logo="header?.linkLogo"
    >
      <template #menu>
        <molecules-ui-menu
          v-if="menu"
          id="main-menu"
          v-model:is-menu-open="isMenuOpen"
          v-model:active-trigger="activeTrigger"
          v-bind="menu"
        />
      </template>
    </organisms-ui-header>
    <molecules-ui-modal
      v-if="header?.ctas && header.ctas[0].popin"
      v-model="isHelpModalOpen"
      v-bind="{ title: header.ctas[0].popin.title }"
      :color="UiTwoToneColor.YELLOW_03"
    >
      <div class="Header__Modal__content color-blue-01">
        <p class="heading-2">{{ header.ctas[0].popin.subtitle }}</p>
        <molecules-ui-rich-text
          v-if="header.ctas[0].popin.content"
          :content="header.ctas[0].popin.content"
        />
        <p
          class="Header__Modal__phone mt-4 heading-2"
          :href="`tel:${header.ctas[0].popin.phoneNumber}`"
        >
          <atoms-ui-icon
            name="phone"
            class="Header__Modal__icon mr-2"
          />
          <span class="mt-1">
            {{ header.ctas[0].popin.phoneNumber }}
          </span>
        </p>
        <p
          v-if="header.ctas[0].popin.caption"
          class="mt-1 paragraph-4"
        >
          {{ header.ctas[0].popin.caption }}
        </p>
      </div>
    </molecules-ui-modal>
  </div>
</template>

<style lang="scss" scoped>
@use 'utilities/mq';

.Common-header {
  z-index: 100;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;

  @include mq.media('>=medium') {
    :deep(.Menu__content__wrapper) {
      padding-inline-start: 212px;
    }
  }
}

.Header__Modal__content {
  text-align: center;
}

.Header__Modal__phone {
  display: inline-flex;
}

.Header__Modal__icon {
  font-size: 3.5rem;
}
</style>
